import { EQUIVALENT_ROUTES, pages } from '@aline/routes';
import { matchRoutes } from 'react-router-dom';

const PAGE_ROUTE_OBJECTS = Object.values(pages).map(path => ({ path }));

/**
 * Checks if two routes refer to the same resource (e.g., same document, same workflow), so that
 * opening one while the other is already open reuses that tab.
 *
 * Handles cases like `/doc/abc` matching `/doc/abc/ai` or `/doc/abc/comments`.
 */
export function getRoutesMatchSameResource(routeA: string, routeB: string): boolean {
  if (routeA === routeB) return true;

  const pathnameA = getPathname(routeA);
  const pathnameB = getPathname(routeB);

  if (
    EQUIVALENT_ROUTES.some(
      ([a, b]) => (pathnameA === a && pathnameB === b) || (pathnameA === b && pathnameB === a)
    )
  )
    return true;

  return getResourceKey(pathnameA) === getResourceKey(pathnameB);
}

function getPathname(route: string): string {
  return route.split(/[?#]/)[0];
}

/**
 * A tab owns a resource, not a URL, so the key is the pathname truncated after its first dynamic
 * segment: `/doc/abc/ai` keys on `/doc/abc` and collapses onto the document's other views.
 *
 * The dynamic segment's position comes from the matched route pattern rather than being assumed to
 * be the second segment — a resource can live deeper (`/workflows/board/:boardId/settings`), and
 * assuming otherwise makes it collide with the list page above it (`/workflows/board`).
 *
 * Routes with no dynamic segment (list pages) key on their whole pathname, and so only ever match
 * themselves.
 */
function getResourceKey(pathname: string): string {
  const pattern = matchRoutes(PAGE_ROUTE_OBJECTS, pathname)?.at(-1)?.route.path;
  if (!pattern) return pathname;

  const patternSegments = pattern.split('/').filter(Boolean);
  const idIndex = patternSegments.findIndex(segment => segment.startsWith(':'));
  if (idIndex === -1) return pathname;

  const segments = pathname
    .split('/')
    .filter(Boolean)
    .slice(0, idIndex + 1);
  return `/${segments.join('/')}`;
}
